/**
 * IMPORT VALIDATION UTILITY
 * 
 * Use these functions during import to clean/validate data BEFORE it goes into the database.
 * This prevents junk values like "????", "N/A", "unknown" from polluting your data.
 * 
 * USAGE IN IMPORTER:
 * 
 * import { cleanFieldValue, isJunkValue } from './importValidation';
 * 
 * // During import processing:
 * const cleanedCollection = cleanFieldValue(row.collection, 'collectionName');
 * // Returns empty string if junk, otherwise trimmed value
 * 
 * // Or check explicitly:
 * if (!isJunkValue(row.collection)) {
 *   item.collectionName = row.collection.trim();
 * }
 */

// ============================================
// JUNK VALUE PATTERNS
// ============================================

/**
 * Patterns that indicate a junk/placeholder value
 * These should be treated as "no value" during import
 */
export const JUNK_PATTERNS: RegExp[] = [
  /^\?+$/,                    // ???? or ??? or ?
  /^n\/?a$/i,                 // N/A, n/a, NA, na
  /^none$/i,                  // none, None, NONE
  /^unknown$/i,               // unknown, Unknown, UNKNOWN
  /^-+$/,                     // - or -- or ---
  /^tbd$/i,                   // TBD, tbd
  /^tba$/i,                   // TBA, tba
  /^\.+$/,                    // ... or .. or .
  /^x+$/i,                    // xxx, XX, x
  /^\(none\)$/i,              // (none)
  /^\[none\]$/i,              // [none]
  /^null$/i,                  // null
  /^undefined$/i,             // undefined
  /^#n\/a$/i,                 // #N/A (Excel error)
  /^#ref!?$/i,                // #REF! (Excel error)
  /^#value!?$/i,              // #VALUE! (Excel error)
  /^#name\??!?$/i,            // #NAME? (Excel error)
  /^#div\/0!?$/i,             // #DIV/0! (Excel error)
  /^\s*$/,                    // whitespace only or empty
  /^0+$/,                     // 0, 00, 000
  /^test$/i,                  // test
  /^asdf+$/i,                 // asdf, asdfasdf (keyboard mash)
  /^placeholder$/i,           // placeholder
  /^temp$/i,                  // temp
  /^todo$/i,                  // todo
  /^fixme$/i,                 // fixme
];

/**
 * Additional patterns for specific fields
 */
export const FIELD_SPECIFIC_JUNK: Record<string, RegExp[]> = {
  // Collection names shouldn't be just numbers (those are probably years)
  collectionName: [
    /^(19|20)\d{2}$/,         // Just a 4-digit year
  ],
  // Brand shouldn't be generic
  brand: [
    /^brand$/i,
    /^maker$/i,
    /^company$/i,
  ],
  // Event source shouldn't be generic event words
  eventSource: [
    /^event$/i,
    /^release$/i,
  ],
};

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Check if a value is considered "junk" (placeholder, error, etc.)
 * 
 * @param value - The value to check
 * @param fieldName - Optional field name for field-specific validation
 * @returns true if the value is junk and should be ignored
 */
export function isJunkValue(value: string | null | undefined, fieldName?: string): boolean {
  // Null/undefined/empty is not "junk" per se, but should be handled
  if (value === null || value === undefined) return false;
  
  const trimmed = value.trim();
  if (trimmed === '') return false;
  
  // Check against global junk patterns
  for (const pattern of JUNK_PATTERNS) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }
  
  // Check field-specific patterns
  if (fieldName && FIELD_SPECIFIC_JUNK[fieldName]) {
    for (const pattern of FIELD_SPECIFIC_JUNK[fieldName]) {
      if (pattern.test(trimmed)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Clean a field value - returns empty string if junk, otherwise trimmed value
 * 
 * @param value - The value to clean
 * @param fieldName - Optional field name for field-specific validation
 * @returns Cleaned value (empty string if junk)
 */
export function cleanFieldValue(value: string | null | undefined, fieldName?: string): string {
  if (value === null || value === undefined) return '';
  
  const trimmed = value.trim();
  
  if (isJunkValue(trimmed, fieldName)) {
    return '';
  }
  
  return trimmed;
}

/**
 * Validate and clean an entire import row
 * Returns a cleaned object with junk values removed
 * 
 * @param row - The raw row data
 * @param fieldMapping - Map of row keys to field names
 * @returns Cleaned row data
 */
export function cleanImportRow(
  row: Record<string, any>,
  fieldMapping: Record<string, string>
): Record<string, any> {
  const cleaned: Record<string, any> = {};
  
  for (const [rowKey, fieldName] of Object.entries(fieldMapping)) {
    const rawValue = row[rowKey];
    
    if (typeof rawValue === 'string') {
      cleaned[fieldName] = cleanFieldValue(rawValue, fieldName);
    } else if (rawValue !== null && rawValue !== undefined) {
      cleaned[fieldName] = rawValue;
    }
  }
  
  return cleaned;
}

/**
 * Get a list of junk values found in a dataset
 * Useful for showing the user what will be cleaned
 * 
 * @param values - Array of values to check
 * @param fieldName - Optional field name
 * @returns Array of { value, isJunk } objects
 */
export function analyzeValues(
  values: string[],
  fieldName?: string
): { value: string; isJunk: boolean }[] {
  const uniqueValues = [...new Set(values)];
  
  return uniqueValues.map(value => ({
    value,
    isJunk: isJunkValue(value, fieldName),
  }));
}

// ============================================
// USAGE EXAMPLE
// ============================================
/*
// In your importer:

import { cleanFieldValue, isJunkValue } from './importValidation';

// When processing a CSV row:
function processImportRow(row: CSVRow): CollectionItem {
  return {
    name: row.name?.trim() || '',
    brand: cleanFieldValue(row.brand, 'brand'),
    collectionName: cleanFieldValue(row.collection, 'collectionName'),
    eventSource: cleanFieldValue(row.event, 'eventSource'),
    // ... etc
  };
}

// Or with the parser:
const parsed = parseCollection(row.collection);
if (!isJunkValue(parsed.collectionName, 'collectionName')) {
  item.collectionName = parsed.collectionName;
}
*/
